import React from 'react';
import { createRoot } from 'react-dom/client';
import CancelFlow, { CancelFlowHandle, CancelFlowProps } from './CancelFlow';
import styles from './styles/main.less';
import defaultConfig from './exports/default-config';
import { CancelFlowConfig } from './types';
import { track } from './smi-bridge';
import { setFocusFallbackRegion, manageFocus } from './utils/manageFocus';
import { flushSync } from 'react-dom';

let isInitialized = false;
let isOpen = false;
let isPreviewMode = false;
let stepChangeListener: CancelFlowProps['onStepChange'];
let root: ReturnType<typeof createRoot> | null = null;
let shadowHost: HTMLElement | null = null;
let shadowRoot: ShadowRoot | null = null;
let configSet = { ...defaultConfig };
let cancelFlowId: string | undefined;
let opener: HTMLElement | null = null;
let focusRegionCandidates: Element[] = [];
let onError: ((error: Error) => void) | undefined;

const cancelFlowRef = React.createRef<CancelFlowHandle>();

const render = () => {
  if (!root) return;

  root.render(
    React.createElement(CancelFlow, {
      ref: cancelFlowRef,
      onClose: handleClose,
      config: configSet,
      cancelFlowId,
      mode: isPreviewMode ? 'preview' : undefined,
      onStepChange: stepChangeListener,
      onError
    })
  );
};

export const initializeCancelFlow = (
  config: CancelFlowConfig,
  options?: {
    mode?: 'preview';
    onStepChange?: CancelFlowProps['onStepChange'];
    onError?: (error: Error) => void;
    cancelFlowId?: string;
  }
) => {
  configSet = config;
  if (options?.cancelFlowId !== undefined) {
    cancelFlowId = options.cancelFlowId;
  }
  if (options?.mode !== undefined) {
    isPreviewMode = options.mode === 'preview';
  }
  if (options?.onStepChange !== undefined) {
    stepChangeListener = options.onStepChange;
  }
  if (options?.onError !== undefined) {
    onError = options.onError;
  }

  if (isInitialized) return;

  try {
    const smiElement = document.querySelector('og-smi');
    if (!smiElement) {
      console.warn('og-smi element not found, cannot initialize CancelFlow');
      return;
    }

    // Create shadow host element
    // in dev, the element may already exist (e.g. on hot reload) -- reuse it
    shadowHost = document.querySelector('og-cancel-flow') || document.createElement('og-cancel-flow');
    shadowHost.id = 'cancel-flow-shadow-host';
    smiElement.prepend(shadowHost);

    // Create shadow root
    shadowRoot = shadowHost.shadowRoot || shadowHost.attachShadow({ mode: 'open' });

    // Create container within shadow DOM
    const container = shadowRoot.querySelector('#cancel-flow-root') || document.createElement('div');
    container.id = 'cancel-flow-root';
    shadowRoot.appendChild(container);

    root = createRoot(container);

    if (!shadowRoot.querySelector('style')) {
      const style = document.createElement('style');
      style.textContent = styles;
      shadowRoot.appendChild(style);
    }

    // do an initial render of the cancel flow so ref is set for later use
    // we need to use flushSync for the case where `openCancelFlow` initializes the cancel flow, so that the ref methods are populated and ready for use
    flushSync(() => render());

    isInitialized = true;
  } catch (e) {
    onError?.(e instanceof Error ? e : new Error(String(e)));
  }
};

export const openCancelFlow = (options: { subscriptionId: string; opener?: HTMLElement }) => {
  try {
    // this generally shouldn't happen, but if initialize was called before the og-smi element was populated, we need to initialize the flow on open
    if (!isInitialized) {
      initializeCancelFlow(configSet);
    }

    track('subscriptioncancel_started', {
      subscriptionId: options.subscriptionId,
      ...(cancelFlowId && { cancelFlowId })
    });
    opener = options.opener || null;
    focusRegionCandidates = setFocusFallbackRegion(opener);
    isOpen = true;
    shadowHost!.dataset.subscriptionId = options.subscriptionId;
    cancelFlowRef.current?.open(options.subscriptionId);
  } catch (e) {
    onError?.(e instanceof Error ? e : new Error(String(e)));
  }
};

const closeCancelFlow = () => {
  cancelFlowRef.current?.close();
};

const handleClose = () => {
  isOpen = false;
  manageFocus(opener, focusRegionCandidates);
  opener = null;
  focusRegionCandidates = [];
};

export const PREVIEW__updateConfig = (config: CancelFlowConfig) => {
  if (!isPreviewMode) {
    console.warn('Cannot update cancel flow config outside of preview mode');
    return;
  }
  configSet = config;
  flushSync(() => render());
};

export const PREVIEW__navigateToStep = (nodeName: string) => {
  if (!isPreviewMode) {
    console.warn('Cannot update step outside of preview mode');
    return;
  }
  cancelFlowRef.current?.navigateToStep(nodeName);
};

// Update styles without reloading the module (used for style-only hot reload in dev)
export const DEV__updateStyles = (css: string) => {
  const styleEl = shadowRoot?.querySelector('style');
  if (styleEl) {
    styleEl.textContent = css;
  }
};

// used for local dev when the library is reopened
export const DEV__reopenCancelFlow = () => {
  if (!isInitialized) {
    initializeCancelFlow(configSet);
  }

  const prevSubscriptionId = shadowHost!.dataset.subscriptionId;
  if (!prevSubscriptionId) return;
  openCancelFlow({ subscriptionId: prevSubscriptionId });
};

window.og = window.og || {};

window.og.cancelFlow = {
  initializeCancelFlow,
  openCancelFlow,
  closeCancelFlow,
  isOpen: () => isOpen,
  PREVIEW__updateConfig,
  PREVIEW__navigateToStep,
  DEV__reopenCancelFlow,
  DEV__updateStyles,
  config: () => configSet
};
